import type { UnifiedAuthStep } from '~kui/types/auth';

import type { AuthAnalyticsMethodValue } from '~/constants/auth';
import type { CompletedFlowResult } from '~/types/login';

import { useLeaveForSchoolAccount } from '~/composables/unified/useLeaveForSchoolAccount';
import { AuthAnalyticsMethod } from '~/constants/auth';
import { signUpStudentMutation } from '~/graphql/documents/core';
import { AdminMfaStatus, LoginFlowType, SiteRoleType } from '~/graphql/generated/graphql';
import { providerLoginChoiceFromQuery } from '~/utils/providerLogin';
import { computeDurationSeconds } from '~/utils/unified/computeDurationSeconds';
import { ENTRY_TRACKING_QUERY_PARAM, parseEntryTrackingFromQuery } from '~/utils/unified/entryTracking';
import { isSchoolAccountEmailError } from '~/utils/unified/schoolAccountEmail';
import { parseSelectedEntityFromQuery, pickSelectedEntityQueryParams } from '~/utils/unified/selectedEntity';
import { buildSignupHandoffLocation } from '~/utils/unified/signupHandoff';
import { readUtmKeyFromQuery, UTM_KEY_QUERY_PARAM } from '~/utils/unified/signupLink';

import { useAuthPasswordLogin } from './useAuthPasswordLogin';
import { useAuthPasswordSetup } from './useAuthPasswordSetup';
import { useAuthSecondFactor } from './useAuthSecondFactor';
import { loginDoorPasswordSetupCopy, LoginDoorSignUpPolicy, loginDoorSignUpPolicy } from './useLoginDoorConfig';
import { resumedUnifiedAuthStep, useSignInContinuation } from './useSignInContinuation';
import { useSocialAuthCallback } from './useSocialAuthCallback';

export interface UnifiedAuthState {
  email: string;
  firstName: string;
  lastName: string;
  flow: LoginFlowType | null;
  /** How the student proved the email, read back by the signup completion event. */
  method: AuthAnalyticsMethodValue | null;
}

export interface UnifiedAuthResetOptions {
  /**
   * Whether the email the link arrived with goes back into the email step. Only the back links
   * keep it; a restart from a step the link led to clears it, since a prefilled email step has
   * never been touched and its submit stays disabled.
   */
  keepPrefilledEmail?: boolean;
}

const STEP_ORDER: readonly UnifiedAuthStep[] = ['email', 'method', 'password', 'otp', 'choose-account', 'mfa-enrol', 'mfa-challenge', 'register-disabled', 'consent', 'name', 'details', 'password-setup', 'school', 'welcome'];

/** An admin signed in through another door still owes the second factor, and finishes it here. */
const ADMIN_MFA_STEPS: Partial<Record<AdminMfaStatus, UnifiedAuthStep>> = {
  [AdminMfaStatus.EnrolmentRequired]: 'mfa-enrol',
  [AdminMfaStatus.ChallengeRequired]: 'mfa-challenge',
};

export const useUnifiedAuthFlow = (roleType: SiteRoleType) => {
  const { $analytics } = useNuxtApp();
  const route = useRoute();
  const client = useUrqlClient();
  const { requestLoginCode, completeLoginProcess, user, isGhostStudent, isLessonGhost, accountStudentRole } = useAuth();
  const { getFlag } = useFeatureFlags();
  const { triggerPostLoginRedirect } = usePostLoginRedirect();
  const { isManageReferred, clear } = useCrossSitesConfig();
  const platformAppConfig = usePlatformAppConfig(route.path);

  const configStore = useConfigStore();
  const signUpPolicy = loginDoorSignUpPolicy(roleType);
  const isStudentDoor = roleType === SiteRoleType.Student;

  const router = useRouter();

  const { email: queryEmail, code: queryCode } = route.query;
  const prefilledEmail = ref(typeof queryEmail === 'string' ? queryEmail.trim() : '');
  const prefilledOtpCode = ref(typeof queryCode === 'string' ? queryCode.trim() : '');
  const hasPrefilled = !!(prefilledEmail.value && prefilledOtpCode.value);
  // Another page, or this one before a reload, verified the code and the server parked the sign-in on its next step.
  const resumedStep = resumedUnifiedAuthStep({ query: route.query, prefilledEmail: prefilledEmail.value });
  // A hand-off's email belongs to the sign-in it resumes, not to the email step, so a back link from
  // there lands on an empty, submittable field.
  const initialEmail = prefilledEmail.value;
  if (resumedStep) {
    prefilledEmail.value = '';
  }

  const { entrySource, entryCta } = parseEntryTrackingFromQuery(route.query);
  const utmKey = readUtmKeyFromQuery(route.query);
  // A social sign-in resumed at the choice of account started before the provider round trip.
  const queryAuthStartTime = Number(route.query.authStartTime);
  const authStartTime = ref(resumedStep === 'choose-account' && queryAuthStartTime > 0 ? queryAuthStartTime : Date.now());

  const isSocialFlow = !!route.query.social_flow;
  const socialProvider = (route.query.social_provider as string) ?? '';
  // An integration provider sign-in its login page handed over to the choice of school account.
  const providerLoginChoice = resumedStep === 'choose-account' ? providerLoginChoiceFromQuery(route.query) : null;
  const socialAuthPending = ref(isSocialFlow);
  const loading = ref(false);
  const pendingAdminMfaStep = roleType === SiteRoleType.Admin && user.value?.adminMfaStatus
    ? ADMIN_MFA_STEPS[user.value.adminMfaStatus]
    : undefined;
  const currentStep = ref<UnifiedAuthStep>(pendingAdminMfaStep ?? resumedStep ?? (hasPrefilled ? 'otp' : 'email'));
  const direction = ref<'forward' | 'backward'>('forward');
  const finalStepComplete = ref(false);
  const globalError = ref<string | null>(null);

  /**
   * Whether this page session created the account it is now signed in as.
   *
   * Every lane that finishes a signup sets it, including the matched-invite one that provisions the
   * student off the OTP verify without ever showing the consent or details steps. Inferring it from
   * the step the visitor came from would miss that lane, and a returning student parked at `school`
   * to finish joining would be indistinguishable from one who just registered.
   */
  const signedUp = ref(false);

  const authState = ref<UnifiedAuthState>({
    email: initialEmail,
    firstName: '',
    lastName: '',
    flow: null,
    method: null,
  });

  /**
   * Whether the server is holding the role context signup will be checked against.
   *
   * Signing up is authorised by a context the verify stamps on the session, not by anything this
   * page carries — so a submit made before that verify has come back is a request the server can
   * only refuse, and refuse identically however many times it is repeated. Retrying a signup is not
   * a recovery; only verifying again is. The flow the verify returned is the proof the context
   * landed, and it is the same proof on both ways in: the OTP verify and the social exchange each
   * report `NEW_ROLE` off the call that wrote the context.
   */
  const roleContextReady = computed(() => authState.value.flow === LoginFlowType.NewRole);

  const goToStep = (step: UnifiedAuthStep) => {
    const currentIndex = STEP_ORDER.indexOf(currentStep.value);
    const newIndex = STEP_ORDER.indexOf(step);
    direction.value = newIndex > currentIndex ? 'forward' : 'backward';
    currentStep.value = step;
  };

  // A student who signed up but abandoned the join keeps a live session with no school role, so the
  // guest guard leaves them on the login route. Resume them at the school step to finish joining —
  // otherwise they're stranded at the email step, where requestLoginCode is itself gated by the
  // cluster-terms middleware. Fires on boot once `04.auth` has populated the self user.
  //
  // An anonymous lesson participant matches that description (a session, no school role) without
  // being that person: they never signed up, so there is no join to finish, and parking them here
  // offers a student with no account a screen about missing invites and a logout button.
  watch(() => user.value, (value) => {
    if (
      isStudentDoor
      && !isSocialFlow
      && currentStep.value === 'email'
      && configStore.isSchoolsCluster
      && value
      && !isLessonGhost.value
      && !platformAppConfig.userHasRequiredRole(value)
    ) {
      goToStep('school');
    }
  }, { immediate: true });

  const goBack = () => {
    const currentIndex = STEP_ORDER.indexOf(currentStep.value);
    const prevStep = STEP_ORDER[currentIndex - 1];
    if (prevStep) {
      direction.value = 'backward';
      currentStep.value = prevStep;
    }
  };

  const resetFlow = ({ keepPrefilledEmail = true }: UnifiedAuthResetOptions = {}) => {
    authState.value = {
      email: keepPrefilledEmail ? prefilledEmail.value : '',
      firstName: '',
      lastName: '',
      flow: null,
      method: null,
    };
    // A link's code is spent once the flow restarts; left in place it prefills the next OTP step.
    prefilledOtpCode.value = '';
    direction.value = 'backward';
    currentStep.value = 'email';
    globalError.value = null;
    signedUp.value = false;
    clear();
  };

  const trackLoginCompleted = (method: string, wasGhostUser: boolean) => {
    const durationSeconds = computeDurationSeconds(authStartTime.value, { maxSeconds: 3600 });
    $analytics.track(EVENTS.UNIFIED_AUTH_LOGIN_COMPLETED, {
      method,
      isNewUser: false,
      wasGhostUser,
      durationSeconds,
      entrySource,
      entryCta,
      roleType,
      ...(providerLoginChoice?.provider === method ? { viaProof: providerLoginChoice.viaProof } : {}),
    });
  };

  /**
   * The session as this flow is allowed to read it: whatever `completeLoginProcess` resolved.
   *
   * That fetch is allowed to come back with nobody — an expired session, a refused `self`, a
   * network error it swallows — and `userHasRequiredRole` reads roles straight off the user it is
   * handed. Asking it about an absent user throws inside the flow, and the throw lands before the
   * login-completed event and the redirect that follow it, so the login silently loses both while
   * the route guard covers for it. No user is no access, said here once, the same way the route
   * guard says it.
   */
  const prepareAuthenticatedSession = async (): Promise<{ wasGhostUser: boolean; hasAccess: boolean }> => {
    const wasGhostUser = isGhostStudent.value ?? false;
    clear();
    await completeLoginProcess();
    const hasAccess = !!user.value && platformAppConfig.userHasRequiredRole(user.value);
    if (!hasAccess) {
      loading.value = false;
    }
    return { wasGhostUser, hasAccess };
  };

  const completeExistingRoleLogin = async (method: AuthAnalyticsMethodValue = AuthAnalyticsMethod.EmailOtp): Promise<CompletedFlowResult> => {
    const { wasGhostUser, hasAccess } = await prepareAuthenticatedSession();
    if (!hasAccess) {
      // On a schools cluster, an authenticated student without a school enrolment is parked
      // on the pick-school step — the guard keeps them out of the app until they hold a school role.
      if (isStudentDoor && configStore.isSchoolsCluster) {
        goToStep('school');
        return { flow: LoginFlowType.ExistingRole, error: null };
      }
      return { flow: null, error: 'access-denied' };
    }

    trackLoginCompleted(method, wasGhostUser);
    finalStepComplete.value = true;

    await triggerPostLoginRedirect();
    return { flow: LoginFlowType.ExistingRole, error: null };
  };

  const secondFactor = useAuthSecondFactor({
    loading,
    currentStep,
    goToStep,
    // The first factor the admin signed in with, so the completed login reports the method they chose.
    completeExistingLogin: () => completeExistingRoleLogin(authState.value.method ?? AuthAnalyticsMethod.EmailOtp),
    reset: resetFlow,
  });

  const reset = (options?: UnifiedAuthResetOptions) => {
    secondFactor.clear();
    resetFlow(options);
  };

  const completeMatchedInviteSignup = async (method: AuthAnalyticsMethodValue, accountCreated: boolean): Promise<CompletedFlowResult> => {
    const { wasGhostUser, hasAccess } = await prepareAuthenticatedSession();
    if (!hasAccess && !configStore.isSchoolsCluster) {
      return { flow: null, error: 'access-denied' };
    }

    authState.value.firstName = accountStudentRole.value?.firstName ?? '';
    authState.value.method = method;

    // The invite can adopt an account the email already held (a teacher, say) instead of opening one.
    signedUp.value = accountCreated;
    $analytics.track(EVENTS.ACCOUNT_REGISTER_COMPLETED);
    $analytics.track(EVENTS.UNIFIED_AUTH_AUTO_SIGNUP_COMPLETED, {
      method,
      entrySource,
      entryCta,
    });
    if (wasGhostUser) {
      $analytics.track(EVENTS.UNIFIED_AUTH_GHOST_CONVERTED, { entrySource, entryCta });
    }

    // On a schools cluster the provisioned student still needs the consented school
    // join — the school step performs it (mirrors completeExistingRoleLogin).
    goToStep(hasAccess ? 'welcome' : 'school');
    loading.value = false;
    return { flow: LoginFlowType.NewRole, error: null };
  };

  /**
   * The arrival entity a login carries to the server, which accepts that school's student invites
   * for the email. Only a door that signs students up sends one.
   */
  const readSelectedEntity = () => (signUpPolicy === LoginDoorSignUpPolicy.Student ? parseSelectedEntityFromQuery(route.query) : null);

  /**
   * The one statement of what a login-code request carries.
   *
   * The emailed link is a fresh page load with none of this page's state, so anything signup needs
   * has to be handed to the server that builds it: it puts these back on the URL. A resend builds
   * the same link as the first send — the student clicks whichever one arrived last, so a resend
   * that carried less would be a lane, a campaign or an entity silently dropped by asking again.
   */
  const buildLoginCodeInput = (email: string) => {
    const referralCode = route.query.referralCode;
    const selectedEntity = readSelectedEntity();

    return {
      email,
      ...(typeof referralCode === 'string' ? { referralCode } : {}),
      ...(selectedEntity ? { selectedEntity } : {}),
      ...(entrySource ? { entrySource } : {}),
      ...(entryCta ? { entryCta } : {}),
      ...(utmKey ? { utmKey } : {}),
    };
  };

  const sendLoginCode = () => requestLoginCode(buildLoginCodeInput(authState.value.email));

  const resendLoginCode = async () => {
    const result = await sendLoginCode();
    if (!result.error) {
      $analytics.track(EVENTS.LOGIN_OTP_RESENT);
    }
    return result;
  };

  const submitEmail = async (email: string) => {
    loading.value = true;
    authState.value.email = email;

    // The schools site always sends the code first, and asks for the password after it when the person has one.
    const passwordLoginEnabled = !configStore.isSchoolsCluster && await getFlag('password-login-enabled');
    if (passwordLoginEnabled) {
      $analytics.track(EVENTS.LOGIN_EMAIL_SUBMITTED);
      goToStep('method');
      loading.value = false;
      return { error: null };
    }

    const { error } = await requestLoginCode(buildLoginCodeInput(email));

    if (error) {
      loading.value = false;
      return { error };
    }

    $analytics.track(EVENTS.LOGIN_EMAIL_SUBMITTED);
    goToStep('otp');
    loading.value = false;
    return { error: null };
  };

  /**
   * The schools site signs a new student up here, at the consent step. On the consumer site a new
   * student finishes in the Blinky sign-up that /login shows in place of the card: the verify has
   * already put the new-role context on the session, so the hand-off carries the email it was
   * stamped for. The code itself is spent.
   */
  const startNewRoleRegistration = async (method: AuthAnalyticsMethodValue, flow: LoginFlowType) => {
    if (signUpPolicy === LoginDoorSignUpPolicy.None) {
      goToStep('register-disabled');
      loading.value = false;
      return { flow, error: null };
    }

    authState.value.method = method;
    $analytics.track(EVENTS.UNIFIED_AUTH_REGISTRATION_STARTED, {
      method,
      entrySource,
      entryCta,
    });
    if (configStore.isSchoolsCluster) {
      goToStep('consent');
      loading.value = false;
      return { flow, error: null };
    }
    finalStepComplete.value = true;
    await navigateTo(buildSignupHandoffLocation(route.query, authState.value.email), { replace: true });
    return { flow, error: null };
  };

  const { goToPostSignupStep, goToPasswordResetStep, submitPasswordSetup, skipPasswordSetup } = useAuthPasswordSetup({
    loading,
    goToStep,
    entrySource,
    entryCta,
    roleType,
    tokenExpiredKey: loginDoorPasswordSetupCopy(roleType).tokenExpiredKey,
  });

  const leaveForSchoolAccountPage = useLeaveForSchoolAccount();
  const leaveForSchoolAccount = async (method: AuthAnalyticsMethodValue) => {
    finalStepComplete.value = true;
    await leaveForSchoolAccountPage({ roleType, method, entrySource, entryCta });
  };

  const { submitOtp, continueSignIn, continueAfterAccountChoice, restartSignIn } = useSignInContinuation({
    roleType,
    authState,
    currentStep,
    loading,
    direction,
    globalError,
    goToStep,
    prefilledEmail,
    reset,
    resumedStep,
    selectedEntity: readSelectedEntity(),
    socialProvider,
    providerLoginChoice,
    goToPasswordResetStep,
    completeExistingRoleLogin,
    completeMatchedInviteSignup,
    startNewRoleRegistration,
    leaveForSchoolAccount,
  });

  const { chooseOtpMethod, choosePasswordMethod, forgotPassword, submitPassword } = useAuthPasswordLogin({
    roleType,
    authState,
    loading,
    goToStep,
    selectedEntity: readSelectedEntity(),
    sendLoginCode,
    continueSignIn,
    restartSignIn,
  });

  const submitName = (firstName: string, lastName: string) => {
    authState.value.firstName = firstName;
    authState.value.lastName = lastName;
    goToStep('details');
  };

  const submitSchool = () => {
    goToStep('welcome');
  };

  /**
   * What a signup that the server refused leaves behind.
   *
   * Nothing was created, and the context this page believed it held is the thing most likely to
   * have been wrong about it — a context that is gone stays gone, so repeating the submit repeats
   * the refusal forever and the student is stuck on a step with no way off it. Giving the context
   * up drops them back to the email step, where verifying again is the one thing that re-establishes
   * it. A transient failure costs a code; every failure has a way out. The email is cleared too, even
   * one that arrived in the link: a prefilled email step has never been touched, so its submit stays
   * disabled. So is the link's code, which is spent and would otherwise prefill the next OTP step.
   */
  const abandonRoleContext = () => {
    authState.value.email = '';
    prefilledOtpCode.value = '';
    authState.value.flow = null;
    loading.value = false;
    goToStep('email');
  };

  /**
   * A social signup already proved itself with the provider, and the account it created has no
   * emailed-code login to replace — so a password is a second secret nobody asked for. Only the
   * email lanes are offered the step.
   */
  const offerPasswordSetup = (token: string | null | undefined) => goToPostSignupStep(isSocialFlow ? null : token);

  const submitDetails = async (stateId: string, birthDate: Date) => {
    if (loading.value || !roleContextReady.value) {
      return { error: null };
    }

    loading.value = true;
    const wasGhostUser = user.value?.isGhostStudent ?? false;
    const method = authState.value.method ?? (isSocialFlow ? `social_${socialProvider.toLowerCase()}` : AuthAnalyticsMethod.EmailOtp);

    // Attribution rides the referral link, not a field the user fills in: the details form no
    // longer asks for a code, so the only source is the query param carried through the flow.
    const referralCode = typeof route.query.referralCode === 'string' ? route.query.referralCode : '';
    const selectedEntity = parseSelectedEntityFromQuery(route.query);

    const result = await client.mutation(signUpStudentMutation, {
      input: {
        firstName: authState.value.firstName,
        lastName: authState.value.lastName,
        email: authState.value.email,
        stateId,
        birthDate,
        isManageReferred: !!isManageReferred.value,
        ...(referralCode ? { referralCode } : {}),
        ...(selectedEntity ? { selectedEntity } : {}),
        ...(entrySource ? { entrySource } : {}),
        ...(entryCta ? { entryCta } : {}),
        ...(utmKey ? { utmKey } : {}),
      },
    });

    if (isSchoolAccountEmailError(result.error)) {
      await leaveForSchoolAccount(method);
      return { error: null };
    }

    if (result.error) {
      abandonRoleContext();
      return { error: result.error };
    }

    await completeLoginProcess();
    signedUp.value = true;
    offerPasswordSetup(result.data?.core?.user?.signUpStudent?.passwordChangeToken);
    loading.value = false;
    $analytics.track(EVENTS.ACCOUNT_REGISTER_COMPLETED);
    $analytics.track(EVENTS.UNIFIED_AUTH_SIGNUP_COMPLETED, {
      method,
      entrySource,
      entryCta,
    });
    if (wasGhostUser) {
      $analytics.track(EVENTS.UNIFIED_AUTH_GHOST_CONVERTED, {
        entrySource,
        entryCta,
      });
    }
    return { error: null };
  };

  const submitConsent = async () => {
    if (loading.value || !roleContextReady.value) {
      return { error: null };
    }

    loading.value = true;
    const wasGhostUser = user.value?.isGhostStudent ?? false;
    const selectedEntity = parseSelectedEntityFromQuery(route.query);
    const referralCode = route.query.referralCode;

    const result = await client.mutation(signUpStudentMutation, {
      input: {
        email: authState.value.email,
        isManageReferred: !!isManageReferred.value,
        ...(typeof referralCode === 'string' ? { referralCode } : {}),
        ...(selectedEntity ? { selectedEntity } : {}),
        ...(entrySource ? { entrySource } : {}),
        ...(entryCta ? { entryCta } : {}),
        ...(utmKey ? { utmKey } : {}),
      },
    });

    if (isSchoolAccountEmailError(result.error)) {
      await leaveForSchoolAccount(authState.value.method ?? AuthAnalyticsMethod.EmailOtp);
      return { error: null };
    }

    if (result.error) {
      abandonRoleContext();
      return { error: result.error };
    }

    await completeLoginProcess();
    signedUp.value = true;
    offerPasswordSetup(result.data?.core?.user?.signUpStudent?.passwordChangeToken);
    loading.value = false;
    $analytics.track(EVENTS.ACCOUNT_REGISTER_COMPLETED);
    $analytics.track(EVENTS.UNIFIED_AUTH_SIGNUP_COMPLETED, {
      method: authState.value.method ?? AuthAnalyticsMethod.EmailOtp,
      entrySource,
      entryCta,
    });
    if (wasGhostUser) {
      $analytics.track(EVENTS.UNIFIED_AUTH_GHOST_CONVERTED, {
        entrySource,
        entryCta,
      });
    }
    return { error: null };
  };

  const finishWelcome = async () => {
    finalStepComplete.value = true;
    await triggerPostLoginRedirect();
  };

  useSocialAuthCallback({ roleType, authState, loading, socialAuthPending, goToStep, authStartTime, entrySource, entryCta });

  if (hasPrefilled) {
    onMounted(() => {
      const preservedQuery: Record<string, string> = {};
      for (const param of ['referralCode', 'redirect'] as const) {
        const value = route.query[param];
        if (typeof value === 'string') {
          preservedQuery[param] = value;
        }
      }
      if (utmKey) {
        preservedQuery[UTM_KEY_QUERY_PARAM] = utmKey;
      }
      const entryTracking = route.query[ENTRY_TRACKING_QUERY_PARAM];
      if ((entrySource || entryCta) && typeof entryTracking === 'string') {
        preservedQuery[ENTRY_TRACKING_QUERY_PARAM] = entryTracking;
      }
      Object.assign(preservedQuery, pickSelectedEntityQueryParams(route.query));
      void router.replace({ path: route.path, query: preservedQuery });
    });
  }

  return {
    currentStep,
    authState,
    loading,
    direction,
    finalStepComplete,
    globalError,
    prefilledOtpCode,
    socialAuthPending,
    roleContextReady,
    signedUp,
    entrySource,
    entryCta,
    authStartTime,
    selectedEntity: readSelectedEntity(),
    goBack,
    reset,
    submitEmail,
    resendLoginCode,
    chooseOtpMethod,
    choosePasswordMethod,
    forgotPassword,
    submitPassword,
    continueAfterAccountChoice,
    restartSignIn,
    submitOtp,
    submitName,
    submitSchool,
    submitDetails,
    submitConsent,
    submitPasswordSetup,
    skipPasswordSetup,
    finishWelcome,
    mfaEnrolment: secondFactor.mfaEnrolment,
    mfaRecoveryCodes: secondFactor.mfaRecoveryCodes,
    mfaLoadFailed: secondFactor.mfaLoadFailed,
    mfaExpired: secondFactor.mfaExpired,
    mfaSignedIn: secondFactor.mfaSignedIn,
    beginMfaEnrolment: secondFactor.beginMfaEnrolment,
    mfaEnrolSubmitCode: secondFactor.mfaEnrolSubmitCode,
    mfaRecoveryCodesSaved: secondFactor.mfaRecoveryCodesSaved,
    mfaSubmitCode: secondFactor.mfaSubmitCode,
    mfaSubmitRecoveryCode: secondFactor.mfaSubmitRecoveryCode,
    mfaSignOut: secondFactor.mfaSignOut,
  };
};
